import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormControl, UntypedFormControl } from '@angular/forms';
import { StringCustomFieldConfig, SharedModule, FormInputComponent, DataService, NotificationService } from '@vendure/admin-ui/core';
import { safeJSONParse } from '../utils';
import { LogoSection } from '../generated';

@Component({
  template: `
    <vdr-card>
      <vdr-form-field [label]="'Store Logo, recommended height: 40 (optional, used on mobile home-page/non-home-page and on desktop non-home-page'">
        <vdr-dynamic-form-input
          [control]="normalLogoControl"
          [def]="{
            type: 'relation',
            entity: 'Asset',
            ui: {
              component: 'relation-form-input',
            }
          }"
        >
        </vdr-dynamic-form-input>
      </vdr-form-field>
      <vdr-form-field [label]="'Store Footer Logo, recommended height: 40 (optional, used on footer section)'">
        <vdr-dynamic-form-input
          [control]="footerLogoControl"
          [def]="{
            type: 'relation',
            entity: 'Asset',
            ui: {
              component: 'relation-form-input',
            }
          }"
        >
        </vdr-dynamic-form-input>
      </vdr-form-field>
      <vdr-form-field [label]="'Store Simple Logo, recommended height: 40 (optional, used on desktop homepage when StoreNameLogo is not set)'">
        <vdr-dynamic-form-input
          [control]="smallLogoControl"
          [def]="{
            type: 'relation',
            entity: 'Asset',
            ui: {
              component: 'relation-form-input',
            }
          }"
        >
        </vdr-dynamic-form-input>
      </vdr-form-field>
      <vdr-form-field [label]="'Store Name Logo, recommended height: 40 (optional)'">
        <vdr-dynamic-form-input
          [control]="nameLogoControl"
          [def]="{
            type: 'relation',
            entity: 'Asset',
            ui: {
              component: 'relation-form-input',
            }
          }"
        >
        </vdr-dynamic-form-input>
      </vdr-form-field>
      <vdr-form-field [label]="'Store Browser Icon(*.ico), recommended resolution: 32x32 (optional)'">
        <vdr-dynamic-form-input
          [control]="browserIconControl"
          [def]="{
            type: 'relation',
            entity: 'Asset',
            ui: {
              component: 'relation-form-input',
            }
          }"
        >
        </vdr-dynamic-form-input>
      </vdr-form-field>
    </vdr-card>
  `,
  standalone: true,
  imports: [SharedModule],
})
export class LogoSectionFormComponent implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit {
  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  logoSectionData: LogoSection;
  normalLogoControl = new UntypedFormControl();
  footerLogoControl = new UntypedFormControl();
  smallLogoControl = new UntypedFormControl();
  nameLogoControl = new UntypedFormControl();
  browserIconControl = new UntypedFormControl();

  constructor(
    private dataService: DataService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    const defaultLogoSectionData: LogoSection = {
        normalLogoId: null,
        footerLogoId: null,
        smallLogoId: null,
        nameLogoId: null,
        browserIconId: null,
        webclipIconId: null,
    };
    this.logoSectionData = safeJSONParse<LogoSection>(
      this.formControl.value,
      this.notificationService,
      defaultLogoSectionData,
    )!;
    if (this.logoSectionData?.normalLogoId) {
      this.dataService.product
        .getAsset(this.logoSectionData?.normalLogoId.toString())
        .mapSingle((data) => data.asset)
        .subscribe((asset) => {
          this.normalLogoControl.setValue(asset);
        });
    }
    if (this.logoSectionData?.footerLogoId) {
      this.dataService.product
        .getAsset(this.logoSectionData?.footerLogoId.toString())
        .mapSingle((data) => data.asset)
        .subscribe((asset) => {
          this.footerLogoControl.setValue(asset);
        });
    }
    if (this.logoSectionData?.smallLogoId) {
      this.dataService.product
        .getAsset(this.logoSectionData.smallLogoId.toString())
        .mapSingle((data) => data.asset)
        .subscribe((asset) => {
          this.smallLogoControl.setValue(asset);
        });
    }
    if (this.logoSectionData?.nameLogoId) {
      this.dataService.product
        .getAsset(this.logoSectionData!.nameLogoId.toString())
        .mapSingle((data) => data.asset)
        .subscribe((asset) => {
          this.nameLogoControl.setValue(asset);
        });
    }
    if (this.logoSectionData?.browserIconId) {
      this.dataService.product
        .getAsset(this.logoSectionData!.browserIconId.toString())
        .mapSingle((data) => data.asset)
        .subscribe((asset) => {
          this.browserIconControl.setValue(asset);
        });
    }
  }

  ngAfterViewInit() {
    this.normalLogoControl.valueChanges.subscribe(this.onNormalLogoChange);
    this.footerLogoControl.valueChanges.subscribe(this.onFooterLogoChange);
    this.smallLogoControl.valueChanges.subscribe(this.onSmallLogoChange);
    this.nameLogoControl.valueChanges.subscribe(this.onNameLogoChange);
    this.browserIconControl.valueChanges.subscribe(this.onBrowserIconChange);
  }

  private onNormalLogoChange = (value: any) => {
    const normalLogoId = value ? value.id : null;
    if (this.logoSectionData?.normalLogoId !== normalLogoId) {
      this.logoSectionData!.normalLogoId = normalLogoId;
      this.updateJsonString();
    }
  };

  private onFooterLogoChange = (value: any) => {
    const footerLogoId = value ? value.id : null;
    if (this.logoSectionData?.footerLogoId !== footerLogoId) {
      this.logoSectionData!.footerLogoId = footerLogoId;
      this.updateJsonString();
    }
  };

  private onSmallLogoChange = (value: any) => {
    const smallLogoId = value ? value.id : null;
    if (this.logoSectionData?.smallLogoId !== smallLogoId) {
      this.logoSectionData!.smallLogoId = smallLogoId;
      this.updateJsonString();
    }
  };

  private onNameLogoChange = (value: any) => {
    const nameLogoId = value ? value.id : null;
    if (this.logoSectionData?.nameLogoId !== nameLogoId) {
      this.logoSectionData!.nameLogoId = nameLogoId;
      this.updateJsonString();
    }
  };

  private onBrowserIconChange = (value: any) => {
    const browserIconId = value ? value.id : null;
    if (this.logoSectionData?.browserIconId !== browserIconId) {
      this.logoSectionData!.browserIconId = browserIconId;
      this.updateJsonString();
    }
  };

  private updateJsonString() {
    this.formControl.setValue(JSON.stringify(this.logoSectionData));
    this.formControl.markAsDirty();
  }
}
